/**
 * Grid particles system for the 3D scene
 */

import type * as THREE_NS from 'three';

export interface GridParticlesResult {
  particles: THREE_NS.Points | THREE_NS.InstancedMesh;
  particleCount: number;
  positions: Float32Array;
  useInstanced: boolean;
}

/**
 * Original Points-based implementation (simpler, less control)
 */
export function createGridParticles(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS
): THREE_NS.Points {
  const {
    BufferGeometry,
    Float32BufferAttribute,
    Points,
    PointsMaterial,
    CanvasTexture,
  } = THREE as any;

  const gridSize = 20;
  const spacing = 8;
  const halfSpacing = spacing / 2; // Half cell height for offset
  const geometry = new BufferGeometry();
  const positions: number[] = [];

  for (let x = 0; x < gridSize; x++) {
    for (let y = 0; y < gridSize; y++) {
      for (let z = 0; z < gridSize; z++) {
        // Skip every other point (checkerboard pattern)
        if ((x + y + z) % 2 === 0) {
          const posX = (x - gridSize / 2) * spacing;
          // Shift grid up by half a cell height so camera isn't directly aligned
          const posY = (y - gridSize / 2) * spacing + halfSpacing;
          const posZ = (z - gridSize / 2) * spacing;
          positions.push(posX, posY, posZ);
        }
      }
    }
  }

  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));

  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = 64;
  textureCanvas.height = 64;
  const ctx = textureCanvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    ctx.fillRect(28, 8, 8, 48);
    ctx.fillRect(8, 28, 48, 8);
  }
  const texture = new CanvasTexture(textureCanvas);

  const material = new PointsMaterial({
    color: 0xffffff,
    size: 0.5,
    transparent: true,
    opacity: 0.8,
    map: texture,
    blending: (THREE as any).NormalBlending,
    depthWrite: false,
    sizeAttenuation: true,
    alphaTest: 0.1,
  });

  const particles = new Points(geometry, material);
  particles.renderOrder = 500;
  scene.add(particles);

  return particles;
}

/**
 * Enhanced InstancedMesh-based implementation (supports per-particle animations)
 * Uses quads (planes) with plus symbol texture for better control
 */
export function createGridParticlesInstanced(
  scene: THREE_NS.Scene,
  THREE: typeof THREE_NS
): GridParticlesResult {
  const {
    BufferGeometry,
    Float32BufferAttribute,
    InstancedMesh,
    PlaneGeometry,
    ShaderMaterial,
    CanvasTexture,
    Matrix4,
    Vector3,
  } = THREE as any;

  const gridSize = 20;
  const spacing = 8;
  const halfSpacing = spacing / 2;
  const positions: number[] = [];
  const particleIndices: number[] = [];

  // Collect all particle positions
  let particleIndex = 0;
  for (let x = 0; x < gridSize; x++) {
    for (let y = 0; y < gridSize; y++) {
      for (let z = 0; z < gridSize; z++) {
        // Skip every other point (checkerboard pattern)
        if ((x + y + z) % 2 === 0) {
          const posX = (x - gridSize / 2) * spacing;
          const posY = (y - gridSize / 2) * spacing + halfSpacing;
          const posZ = (z - gridSize / 2) * spacing;
          positions.push(posX, posY, posZ);
          particleIndices.push(particleIndex++);
        }
      }
    }
  }

  const particleCount = positions.length / 3;
  const positionsArray = new Float32Array(positions);

  // Create plus symbol texture
  const textureCanvas = document.createElement('canvas');
  textureCanvas.width = 64;
  textureCanvas.height = 64;
  const ctx = textureCanvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = 'rgba(255, 255, 255, 1)';
    ctx.fillRect(28, 8, 8, 48);
    ctx.fillRect(8, 28, 48, 8);
  }
  const texture = new CanvasTexture(textureCanvas);
  texture.needsUpdate = true;

  // Create a single quad geometry for each instance
  // Size matches original Points size (0.5) for consistency
  const quadGeometry = new PlaneGeometry(0.5, 0.5);

  // Create instance matrices
  const matrices: any[] = [];
  const colors = new Float32Array(particleCount * 3);
  const scales = new Float32Array(particleCount);
  const rotations = new Float32Array(particleCount);

  for (let i = 0; i < particleCount; i++) {
    const matrix = new Matrix4();
    const x = positions[i * 3];
    const y = positions[i * 3 + 1];
    const z = positions[i * 3 + 2];
    
    matrix.setPosition(x, y, z);
    matrices.push(matrix);

    // Initialize colors (white)
    colors[i * 3] = 1;
    colors[i * 3 + 1] = 1;
    colors[i * 3 + 2] = 1;
    
    // Initialize scales
    scales[i] = 1;
    
    // Initialize rotations
    rotations[i] = 0;
  }

  // Create shader material that supports per-instance color and rotation
  // Note: Attributes must match the attribute names set on the geometry
  const vertexShader = `
    attribute vec3 instanceColor;
    attribute float instanceScale;
    attribute float instanceRotation;
    
    varying vec3 vColor;
    varying vec2 vUv;
    
    void main() {
      vUv = uv;
      // Use instanceColor if available, otherwise white
      vColor = instanceColor;
      
      vec3 pos = position;
      
      // Apply rotation around Z axis (for backflip effect)
      // Note: Rotation is applied in local space before instanceMatrix
      float c = cos(instanceRotation);
      float s = sin(instanceRotation);
      mat2 rot = mat2(c, -s, s, c);
      pos.xy = rot * pos.xy;
      
      // Apply scale (default to 1.0 if attribute not available)
      float scale = instanceScale;
      if (scale < 0.01) scale = 1.0; // Fallback
      pos *= scale;
      
      // Transform through instance matrix (billboard) then model-view
      vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(pos, 1.0);
      gl_Position = projectionMatrix * mvPosition;
    }
  `;

  const fragmentShader = `
    uniform sampler2D uTexture;
    varying vec3 vColor;
    varying vec2 vUv;
    
    void main() {
      vec4 texColor = texture2D(uTexture, vUv);
      // Only render if texture has alpha (plus symbol)
      if (texColor.a < 0.1) {
        discard;
      }
      gl_FragColor = vec4(vColor * texColor.rgb, texColor.a * 0.8);
    }
  `;

  const material = new ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uTexture: { value: texture },
    },
    transparent: true,
    blending: THREE.NormalBlending,
    depthWrite: false,
    side: THREE.DoubleSide, // Render both sides of the plane
    alphaTest: 0.1,
  });

  // Create instanced mesh
  const instancedMesh = new InstancedMesh(quadGeometry, material, particleCount);

  // Set instance matrices
  for (let i = 0; i < particleCount; i++) {
    instancedMesh.setMatrixAt(i, matrices[i]);
  }

  // Add per-instance attributes (must be marked as instanced)
  const colorAttr = new Float32BufferAttribute(colors, 3);
  colorAttr.setUsage(THREE.DynamicDrawUsage);
  instancedMesh.geometry.setAttribute('instanceColor', colorAttr);
  
  const scaleAttr = new Float32BufferAttribute(scales, 1);
  scaleAttr.setUsage(THREE.DynamicDrawUsage);
  instancedMesh.geometry.setAttribute('instanceScale', scaleAttr);
  
  const rotationAttr = new Float32BufferAttribute(rotations, 1);
  rotationAttr.setUsage(THREE.DynamicDrawUsage);
  instancedMesh.geometry.setAttribute('instanceRotation', rotationAttr);
  
  // Mark attributes as instanced (critical for InstancedMesh)
  instancedMesh.geometry.attributes.instanceColor.setUsage(THREE.DynamicDrawUsage);
  instancedMesh.geometry.attributes.instanceScale.setUsage(THREE.DynamicDrawUsage);
  instancedMesh.geometry.attributes.instanceRotation.setUsage(THREE.DynamicDrawUsage);

  // Store positions array on mesh for billboard effect
  (instancedMesh as any).__particlePositions = positionsArray;

  // Make quads always face camera (billboard effect)
  // Note: Scale and rotation are handled by shader attributes, not the matrix
  instancedMesh.onBeforeRender = (renderer: any, scene: any, camera: any) => {
    const cameraWorldPos = new Vector3();
    camera.getWorldPosition(cameraWorldPos);
    const storedPositions = (instancedMesh as any).__particlePositions as Float32Array;
    
    for (let i = 0; i < particleCount; i++) {
      const matrix = new Matrix4();
      const x = storedPositions[i * 3];
      const y = storedPositions[i * 3 + 1];
      const z = storedPositions[i * 3 + 2];
      const particlePos = new Vector3(x, y, z);
      
      // Billboard: make quad face camera
      // lookAt(eye, target, up) - we want quad at particlePos to face camera
      matrix.lookAt(particlePos, cameraWorldPos, new Vector3(0, 1, 0));
      matrix.setPosition(particlePos);
      
      instancedMesh.setMatrixAt(i, matrix);
    }
    
    instancedMesh.instanceMatrix.needsUpdate = true;
  };

  instancedMesh.renderOrder = 500;
  scene.add(instancedMesh);

  // Store references for animation updates
  (instancedMesh as any).__particleColors = colors;
  (instancedMesh as any).__particleScales = scales;
  (instancedMesh as any).__particleRotations = rotations;

  return {
    particles: instancedMesh,
    particleCount,
    positions: positionsArray,
    useInstanced: true,
  };
}
